import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "lib/api-client";
import { toast } from "react-toastify";

//------------------------------------------------------------  CLIENT TABLE DATA API ------------------------------------------------------------------\\

const fetchClientCompanies = async ({ pageParam = 1, queryKey }) => {
  const [_key, search, sort] = queryKey;

  const url = search ? `/api/v9/myBinnys/getAllClientCompanys?searchBy=${search}` : `/api/v9/myBinnys/getAllClientCompanys`;

  const response = await apiClient.post(url, {
    page: pageParam,
    limit: 10,
    sortBy: sort?.sortBy,
    order: sort?.order,
  });

  const { data, pagination, totalClients, totalUsers  } = response.data;

  return {
    totalClients: totalClients,
    totalUsers: totalUsers,
    clients: data || [],
    currentPage: pagination.page,
    totalPages: pagination.totalPages,
  };
};

export const useClientCompanies = (search = "", sort = null) =>
  useInfiniteQuery({
    queryKey: ["clientCompanies", search, sort],
    queryFn: fetchClientCompanies,
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

//------------------------------------------------------------  USER TABLE DATA API ------------------------------------------------------------------\\

const fetchClientCompanyEmployees = async ({ pageParam = 1, queryKey }) => {
  const [_key, clientId, searchTerm] = queryKey;

  const url = searchTerm ? `/api/v9/myBinnys/getClientCompanyEmployees/${clientId}?searchBy=${searchTerm}` : `/api/v9/myBinnys/getClientCompanyEmployees/${clientId}`;

  const response = await apiClient.post(url, {
    page: pageParam,
    limit: 10,
  });

  const { data, pagination } = response.data;

  return {
    employees: data || [],
    currentPage: pagination.page,
    totalPages: pagination.totalPages,
  };
};

export const useClientCompanyEmployees = (clientId, searchTerm = "") =>
  useInfiniteQuery({
    queryKey: ["clientUsers", clientId, searchTerm],
    queryFn: fetchClientCompanyEmployees,
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: !!clientId,
  });

// Get Report Table API -----------------------------------------

const apiData = async () => {
  const res = await apiClient.get("/api/v9/myBinnys/getReportsListData");
  return res?.data?.data || [];
};

const apiGetCompanyReportData = async (clientId) => {
  try {
    const res = await apiClient.get(`/api/v9/myBinnys/reportList/${clientId}`);
    return res?.data?.data || [];
  } catch (error) {
    return [];
  }
};

export const useReportsList = (clientId) => {
  return useQuery({
    queryKey: ["reports", clientId],
    queryFn: async () => {
      const [allReports, companyReports] = await Promise.all([apiData(), apiGetCompanyReportData(clientId)]);
      return allReports
        .map((report) => {
          if (report?.reportType === "Order") {
            return {
              sectionTitle: "Reports (Quotes & Orders)",
              order: 1,
              data: report?.reports?.map((item) => ({
                ...item,
                name: item?.ReportName,
                access: companyReports?.includes(item?.ReportId),
              })),
            };
          }

          if (report?.reportType === "Transaction") {
            return {
              sectionTitle: "Reports (Transaction)",
              order: 2,
              data: report?.reports?.map((item) => ({
                ...item,
                name: item?.ReportName,
                access: companyReports.includes(item?.ReportId),
              })),
            };
          }

          return null;
        })
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
    },
    enabled: !!clientId,
  });
};

//Update Report Table API:-----------------

const updateReportListAPI = async ({ clientId, reportListData }) => {
  const response = await apiClient.patch(`/api/v9/myBinnys/reportList/${clientId}`, { reportListData });
  return response?.data;
};

export const useUpdateReportListData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateReportListAPI,
    onSuccess: (data, variables) => {
      if (data?.status === 200) {
        toast.success(`${data?.message}`, {
          position: "top-right",
          autoClose: 2000,
        });
        queryClient.invalidateQueries({ queryKey: ["reports", variables?.clientId] });
      } else {
        toast.error(`${data?.message} || "Something went wrong ❌"`, {
          position: "top-right",
          autoClose: 2000,
        });
      }
    },
    onError: (error) => {
      toast.error(`${error?.message} || "Network error"`, {
        position: "top-right",
        autoClose: 2000,
      });
    },
  });
};

//------------------------------------------------------------  USER ACCESS (APP RIGHTS) API ------------------------------------------------------------------\\

const fetchUserAccess = async (employeeCompanyId) => {
  const res = await apiClient.get(`/api/v9/myBinnys/visionClientCompanyEmployee/rights/${employeeCompanyId}`);
  return res?.data?.data || {};
};

export const useUserAccess = (employeeCompanyId) =>
  useQuery({
    queryKey: ["userAccess", employeeCompanyId],
    queryFn: () => fetchUserAccess(employeeCompanyId),
    enabled: !!employeeCompanyId,
    retry: 1,
  });

const updateUserAccessAPI = async ({ employeeCompanyId, rights }) => {
  const res = await apiClient.patch(`/api/v9/myBinnys/visionClientCompanyEmployee/rights/${employeeCompanyId}`, { rights });
  return res?.data;
};

export const useUpdateUserAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserAccessAPI,
    onSuccess: (data, variables) => {
      toast.success(`${data?.message || "Access updated"}`, { position: "top-right", autoClose: 2000 });
      queryClient.invalidateQueries({ queryKey: ["userAccess", variables?.employeeCompanyId] });
    },
    onError: (error) => {
      toast.error(`${error?.response?.data?.message || error?.message || "Something went wrong"}`, {
        position: "top-right",
        autoClose: 2000,
      });
    },
  });
};

// Toggle API :-
const toggleApi = async (payload) => {
  const clientId = payload?.clientId || payload?._id;
  const action = payload?.action;
  const actionQuery = action ? `&action=${encodeURIComponent(action)}` : "";
  const responce = await apiClient.patch(`/api/v9/myBinnys/togglePurchasesPageAccess?id=${clientId}${actionQuery}`);
  return responce?.data;
};

export const useToggleUpdatePurchaseAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleApi,
    onSuccess: (data) => {
      if (data?.status === 200) {
        toast.success(`${data?.message}`, {
          position: "top-right",
          autoClose: 2000,
        });
        queryClient.invalidateQueries("clientCompanies");
      } else {
        toast.error(`${data?.message} || "Something went wrong ❌"`, {
          position: "top-right",
          autoClose: 2000,
        });
      }
    },
    onError: (error) => {
      toast.error(`${error?.message} || "Network error"`, {
        position: "top-right",
        autoClose: 2000,
      });
    },
  });
};
